import React, { useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { btn } from '../utils/buttonStyles';

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmStyle = 'danger', // 'danger', 'warning', 'primary'
  icon: CustomIcon = null,
  loading = false,
}) => {
  const { theme } = useTheme();

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const confirmButtonStyles = {
    danger: theme.buttonDanger,
    warning: 'bg-yellow-500 hover:bg-yellow-600 text-white',
    primary: theme.buttonPrimary,
  };

  const iconStyles = {
    danger: 'bg-red-100 text-red-600',
    warning: 'bg-yellow-100 text-yellow-600',
    primary: 'bg-blue-100 text-blue-600',
  };

  const Icon = CustomIcon || AlertTriangle;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <div
        className={`${theme.card} rounded-xl max-w-sm w-full p-6 shadow-xl my-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${iconStyles[confirmStyle]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>

        {/* Title */}
        <h3 className={`text-lg font-semibold ${theme.text} text-center mb-2`}>
          {title}
        </h3>

        {/* Message */}
        <p className={`${theme.textSecondary} text-center text-sm mb-6`}>
          {message}
        </p>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonGhost}`}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 ${btn.base} ${btn.md} ${confirmButtonStyles[confirmStyle]} flex items-center justify-center`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
